import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Package, Loader, XCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const OrderSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [orderStatus, setOrderStatus] = useState('loading');
  const [orderDetails, setOrderDetails] = useState(null);
  const { clearCart } = useCart();

  const verifyPayment = useCallback(async () => {
    if (!sessionId) {
      setOrderStatus('error');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/payment/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid' || response.data.status === 'completed') {
        setOrderStatus('success');
        setOrderDetails(response.data);
        clearCart();
      } else if (response.data.status === 'expired') {
        setOrderStatus('expired');
      } else {
        // Still pending, poll again
        setTimeout(verifyPayment, 2000);
      }
    } catch {
      setOrderStatus('error');
    }
  }, [sessionId, clearCart]);

  useEffect(() => {
    verifyPayment();
  }, [verifyPayment]);

  if (orderStatus === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm p-8 text-center">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Loader className="w-12 h-12 text-blue-600 animate-spin" />
          </div>
          <h1 className="text-3xl font-heading font-semibold mb-4">Verifying Payment...</h1>
          <p className="text-gray-600">Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  if (orderStatus === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-3xl font-heading font-semibold mb-4">Payment Successful!</h1>
          <p className="text-gray-600 mb-6">
            Thank you for your purchase. Your order has been confirmed and you will receive a confirmation email shortly.
          </p>
          {orderDetails?.order_id && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600 mb-1">Order ID</p>
              <p className="font-mono font-medium">{orderDetails.order_id.substring(0, 16)}...</p>
            </div>
          )}
          <div className="space-y-3">
            <Link to="/orders" className="block">
              <Button className="w-full bg-medical-primary hover:bg-medical-primary/90" data-testid="view-orders-button">
                <Package className="w-4 h-4 mr-2" />
                View Orders
              </Button>
            </Link>
            <Link to="/shop" className="block">
              <Button variant="outline" className="w-full">Continue Shopping</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-sm p-8 text-center">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-12 h-12 text-red-600" />
        </div>
        <h1 className="text-3xl font-heading font-semibold mb-4">Payment Issue</h1>
        <p className="text-gray-600 mb-6">
          {orderStatus === 'expired'
            ? 'Your payment session has expired. Please try again.'
            : 'There was an issue processing your payment. Please try again or contact support.'}
        </p>
        <div className="space-y-3">
          <Link to="/cart" className="block">
            <Button className="w-full bg-medical-primary hover:bg-medical-primary/90">Return to Cart</Button>
          </Link>
          <Link to="/contact" className="block">
            <Button variant="outline" className="w-full">Contact Support</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;